import React, { useCallback, useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { AppState } from '@om-fe/core/core/types';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import { Breadcrumbs } from '../components/breadcrumbs/breadcrumbs';
import { Breakpoints } from '@om-fe/core/core/store/breakpoints';
import { getMainMarketsLimit, getMainMarketsLimitByWidth } from '@om-fe/core/components/match-list/utils/match-list.utils';
import { PlatformTypes, isBrowser } from '@om-fe/utils';
import { RouteNames, RouteDetails, EMRoutingContextInterface, EMRoutingContext } from '@om-fe/core/core/routing/utils';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { MAIN_MARKET_MIN_LIMIT } from '@om-fe/core/components/match-list/utils/match-list.constants';
import { UseSportsNavStore, useSportsNavTopicStoreCreator } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav.hooks';
import { SportsNav } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav';
import { SportsNavType, SportsNavCounterType } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav.constants';
import { LayoutType } from '@om-fe/widgets/dist/components/nav/nav.constants';
import { SportRouteParams } from '@om-fe/widgets/dist/common/routing/routing.constants';
import { DisciplineLiveStatus, TournamentParams } from '@om-fe/models';
import { MixedGroupedTournament } from '../components/mixed-grouped/containers/mixed-grouped-tournament/mixed-grouped-tournament';
import { setVirtualStateExtraParams } from '@om-fe/core/core/routing/custom-routes/custom-routes.utils';
import ParentNav from '@om-fe/core/components/nav-secondary/parent-nav/parent-nav';
import { SportsNavStateModel } from '@om-fe/core/components/sports-nav/sports-nav.constants';
import { parentSlice } from '@om-fe/core/components/sport-parent/sport-parent.constants';
import { navBem } from '@om-fe/widgets/dist/components/nav/nav.utils';
import getSliceName from '@om-fe/core/utils/get-slice-name';
import getSlice from '@om-fe/core/utils/get-slice';
import { RoutingModel } from '@om-fe/core/store/routing';
import { getDisciplineRouteDetails } from '@om-fe/core/components/discipline-anchor/discipline-anchor.utils';
import { getDisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import { useEMRouting } from '@om-fe/core/core/routing/with-routing';
import { useReady } from '@om-fe/store';
import { ParentNavItemRouteParamsMap } from '@om-fe/core/components/nav-secondary/parent-nav/parent-nav-item';
import { SportNavItemModel } from '@om-fe/widgets/dist/widgets/sports-menu/event-navigation-menu/event-navigation-menu.types';
import { MixedGroupedOddsOutrightsTabSelected } from '../components/mixed-grouped/components/tabs/mixed-grouped-odds-outrights-tabs.types';
import { useHistory } from 'react-router-dom';
import { History } from 'history';

interface SportParentPageStoreProps {
    readonly isLarge?: boolean;
    readonly breakpoints?: Breakpoints;
    readonly deviceType?: PlatformTypes;
    readonly routing: RoutingModel;
    readonly firstItemDiscipline: SportNavItemModel;
}

export type SportParentPageProps = SportParentPageStoreProps
    & RouteComponentProps<{
        readonly disciplineId: string;
        readonly disciplineName: string;
        readonly sportChildId?: string;
        readonly sportChildName?: string;
}>;

function SportParentPageComponent(
    {
        match: {
            params: {
                disciplineId,
                disciplineName,
                sportChildId,
            },
        },
        isLarge,
        breakpoints,
        deviceType,
        firstItemDiscipline,
    }: SportParentPageProps,
) {
    let sportPageType: DisciplineRouteType;
    const config: OMFE2ConfigModel = getConfig();
    const topicTimezoneOffset: boolean = config.matchListConfig?.topBets?.topicTimezoneOffset ?? false;
    const enabledBetSimulate: boolean = config.simulate?.enabled ?? false;

    const [tournamentId, setTournamentId] = useState('0');
    const useStore: UseSportsNavStore = useSportsNavTopicStoreCreator({
        sportsNavType: SportsNavType.All,
        counterType: SportsNavCounterType.NumberOfEvents,
        showFromAPI: true,
        topicTimezoneOffset,
        enabledBetSimulate,
    });
    const ready: boolean = useReady(useStore);
    const [activeTab, setActiveTab] = useState<string | undefined>(undefined);
    const [activeParent, setActiveParent] = useState<string | undefined>(undefined);
    const { gettext } = useI18NContext();
    const { getFullPath } = useEMRouting();
    const enabledDisciplinePage: boolean = config?.enabledDisciplinePage ?? false;
    const { getPath } = useContext<EMRoutingContextInterface>(EMRoutingContext);
    const history: History = useHistory();

    const mainMarketsLimit: number = config.mainMarketsLimit?.responsive
        ? config.mainMarketsLimit?.byWidth && (isBrowser() && window)
            ? getMainMarketsLimitByWidth(window.innerWidth, config.mainMarketsLimit?.breakpointsByWidth)
            : getMainMarketsLimit(breakpoints)
        : MAIN_MARKET_MIN_LIMIT;

    const generateSportsPageUrl = useCallback((params: SportRouteParams): string | undefined => {
        const routeDetails: RouteDetails = getDisciplineRouteDetails({
            childId: params.childSportId,
            childName: params.childSportName,
            disciplineId: params.sportId,
            disciplineName: params.sportName,
            isLive: false,
            disciplineRouteType: getDisciplineRouteType(params),
            gettext,
        });

        return getFullPath(routeDetails.routeName, routeDetails.routeParams);
    }, [getFullPath, gettext]);

    const handleParent = (parentId: string) => {
        setActiveParent(parentId);
    }

    const handleSetTournamentParams = useCallback((data: TournamentParams) => {
        setTournamentId(data.tournamentId)
        setActiveTab(data.activeTab);
        setVirtualStateExtraParams(data);
    }, []);

    const handleActiveTabOutrights = useCallback(() => {
        setActiveTab(MixedGroupedOddsOutrightsTabSelected.Outrights);
    }, []);

    useEffect(() => {
        setActiveTab(undefined);
        setTournamentId('0');
    }, [disciplineId, sportChildId]);

    const routeParamsMap: ParentNavItemRouteParamsMap = {
        sportChildName: 'sportChildName',
        sportChildId: 'sportChildId',
        disciplineId: 'disciplineId',
        disciplineName: 'disciplineName',
    };

    const getTournamentParams: TournamentParams = {
        activeTab: activeTab,
        tournamentId: tournamentId,
        disciplineId: disciplineId,
        disciplineName: disciplineName,
        locationId: sportChildId,
    };

    if (ready && sportChildId === '0' && firstItemDiscipline?.id && isBrowser()) {
        const isHyphenatedFullUrl: boolean = !!getConfig().enableHyphenatedUrl?.fullName;
        const sportChildName = isHyphenatedFullUrl ? firstItemDiscipline?.name : firstItemDiscipline?.shortName;

        history.replace(getPath(RouteNames.sportParent, {
            disciplineName,
            disciplineId,
            sportChildName,
            sportChildId: firstItemDiscipline?.id,
        }));
    }

    return (
        <div>
            <Breadcrumbs
                disciplineId={disciplineId}
                isLarge={isLarge}
                routeType={sportPageType}
            />
                {ready &&
                    <div className={'NavWrapper'}>
                        <SportsNav
                            activeItemId={disciplineId}
                            sportRoute={generateSportsPageUrl}
                            useStore={useStore}
                            className={`${SportsNavType.Popular}SportsNav`}
                            showIcon
                            hasArrows={deviceType === 'desktop'}
                            layoutType={LayoutType.Main}
                        />
                    </div>
                }
                <div className={navBem()}>
                    <ParentNav
                        disciplineId={disciplineId}
                        disciplineName={disciplineName}
                        liveStatus={DisciplineLiveStatus.NOT_LIVE}
                        routeName={RouteNames.sportParent}
                        routeParamsMap={routeParamsMap}
                        genericId={activeParent}
                        preventRedirect={false}
                        noRedirectFilter={handleParent}
                        instanceId={`ParentNavSlice-${disciplineId}`}
                        hasArrows={deviceType === 'desktop'}
                        hideAll={true}
                    />     
                </div>
                {sportChildId && (
                    <MixedGroupedTournament
                        disciplineId={sportChildId}
                        tournamentId={tournamentId}
                        sportPageType={DisciplineRouteType.Parent}
                        activeTab={activeTab}
                        mainMarketsLimit={mainMarketsLimit}
                        deviceType={deviceType}
                        handleTournamentParams={handleSetTournamentParams}
                        tournamentParams={getTournamentParams}
                        handleActiveTabOutrights={handleActiveTabOutrights}
                        navLayout={enabledDisciplinePage}
                    />
                )}
        </div>
    );
}

const SportParent: React.ComponentType<SportParentPageStoreProps> = connect<SportParentPageStoreProps, null, SportParentPageProps, AppState>(
    (state: AppState, props: SportParentPageProps): SportParentPageStoreProps => {
        const { match: { params: { disciplineId } } } = props;
        const sliceName: string = getSliceName(parentSlice, `ParentNavSlice-${disciplineId}`);
        const slice: SportsNavStateModel | undefined = getSlice<SportsNavStateModel>(state, sliceName);
        const firstIdDiscipline: string | undefined = slice?.parts?.notLive?.list[0];

        return {
            isLarge: (state.breakpoints.large || state.breakpoints.xl),
            breakpoints: state.breakpoints,
            deviceType: state.deviceInfo?.platform.type,
            routing: state.routing,
            firstItemDiscipline: slice?.parts?.notLive?.items[firstIdDiscipline],
        };
    })(SportParentPageComponent);
export default SportParent;
